// Google Drive sync via Google Identity Services + Google Drive API v3
// No backend needed - uses client-side OAuth2 with PKCE

const SCOPES = 'https://www.googleapis.com/auth/drive.appdata https://www.googleapis.com/auth/drive.file';
const BACKUP_FILENAME = 'allergoaway_backup.json';
let tokenClient: any = null;
let accessToken: string | null = null;

// Store client ID in localStorage so user can configure it
export function getClientId(): string | null {
  return localStorage.getItem('allergoaway_google_client_id');
}

export function setClientId(clientId: string) {
  localStorage.setItem('allergoaway_google_client_id', clientId);
}

function loadGsiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).google?.accounts) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Impossibile caricare Google Identity Services'));
    document.head.appendChild(script);
  });
}

export async function initGoogleDrive(): Promise<boolean> {
  const clientId = getClientId();
  if (!clientId) return false;

  try {
    await loadGsiScript();
    return true;
  } catch (e) {
    console.error('Failed to init Google Drive:', e);
    return false;
  }
}

export async function authenticateGoogle(): Promise<string | null> {
  const clientId = getClientId();
  if (!clientId) throw new Error('Client ID Google non configurato');

  await loadGsiScript();

  return new Promise((resolve, reject) => {
    tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPES,
      callback: (response: any) => {
        if (response.error) {
          reject(new Error(response.error));
          return;
        }
        accessToken = response.access_token;
        localStorage.setItem('allergoaway_gdrive_token', accessToken!);
        resolve(accessToken);
      },
    });
    tokenClient.requestAccessToken({ prompt: 'consent' });
  });
}

function getStoredToken(): string | null {
  return localStorage.getItem('allergoaway_gdrive_token');
}

async function getValidToken(): Promise<string> {
  let token = getStoredToken();
  if (token) {
    // Verify token is still valid
    try {
      const res = await fetch('https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=' + token);
      if (res.ok) {
        accessToken = token;
        return token;
      }
    } catch {}
  }
  // Token expired or invalid, need to re-authenticate
  const newToken = await authenticateGoogle();
  if (!newToken) throw new Error('Autenticazione fallita');
  return newToken;
}

async function findBackupFile(token: string): Promise<string | null> {
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=name='${BACKUP_FILENAME}' and trashed=false&spaces=appDataFolder,drive&fields=files(id,name,modifiedTime)`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) return null;
  const data = await res.json();
  return data.files?.[0]?.id || null;
}

export async function uploadToGoogleDrive(data: object): Promise<{ success: boolean; error?: string }> {
  try {
    const token = await getValidToken();
    const jsonStr = JSON.stringify(data);
    const existingFileId = await findBackupFile(token);

    const metadata = {
      name: BACKUP_FILENAME,
      mimeType: 'application/json',
      ...(!existingFileId ? { parents: ['appDataFolder'] } : {}),
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', new Blob([jsonStr], { type: 'application/json' }));

    const url = existingFileId
      ? `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=multipart`
      : 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';

    const res = await fetch(url, {
      method: existingFileId ? 'PATCH' : 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });

    if (!res.ok) {
      const err = await res.text();
      return { success: false, error: `Upload fallito: ${err}` };
    }

    localStorage.setItem('allergoaway_last_sync', new Date().toISOString());
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
}

export async function downloadFromGoogleDrive(): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const token = await getValidToken();
    const fileId = await findBackupFile(token);

    if (!fileId) {
      return { success: false, error: 'Nessun backup trovato su Google Drive' };
    }

    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (!res.ok) {
      return { success: false, error: 'Download fallito' };
    }

    const data = await res.json();
    return { success: true, data };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
}

export function getLastSyncTime(): string | null {
  return localStorage.getItem('allergoaway_last_sync');
}

export function isGoogleDriveConfigured(): boolean {
  return !!getClientId();
}

// Auto-sync setup
let syncInterval: ReturnType<typeof setInterval> | null = null;

export function startAutoSync(syncFn: () => Promise<void>, intervalMinutes: number) {
  stopAutoSync();
  syncInterval = setInterval(syncFn, intervalMinutes * 60 * 1000);
}

export function stopAutoSync() {
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
  }
}
